import { session } from 'auth/session'
import { useState } from 'react'
import { useDebounce } from 'react-use'
import { apiClient } from '../api'
import { getErrorMessage } from '../utils/getErrorMessage'

const me = session.get('user')

export const useCreateOrg = ({ orgName }) => {
  const [errorMsg, setErrorMsg] = useState('')
  const [isValid, setIsValid] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  /**
   * Create a new org view is driven based on this query param being set
   */

  useDebounce(
    async () => {
      if (!orgName) {
        setErrorMsg('')
        setIsValid(false)
        return
      }
      setIsValidating(true)
      /**
       * Validate the org name
       */
      try {
        const { validationErrors } = await apiClient({
          method: 'post',
          url: '/core/validate/tenants',
          data: {
            ownerUserName: me.userName,
            title: orgName,
            tenantName: orgName,
          },
        })

        if (validationErrors?.length > 0) {
          let error = validationErrors[0].errorMessage

          // Error messages from the back-end are confusing.  Make them human friendly.
          error = error.includes(' - ') ? error.split(' - ')[0] : error

          // Character length
          error = error.includes('length must be less than')
            ? 'org name must be less than 40 characters'
            : error

          // Invalid characters
          error = error.includes('may contain only')
            ? 'org name may contain only numbers and lowercase letters'
            : error

          // Name is taken
          error = error.includes('already exists') ? 'org name is already taken.' : error

          setErrorMsg(error)
        } else {
          setErrorMsg('')
        }

        setIsValid(validationErrors?.length === 0)
      } catch (e) {
        setIsValid(false)
      }
      setIsValidating(false)
    },
    250,
    [orgName]
  )

  /**
   * Creates a new stage and corresponding deployment profile
   */
  const createOrg = async ({ redirect = true } = {}) => {
    if (!orgName || !isValid) {
      return
    }

    setIsCreating(true)

    try {
      setIsCreating(false)

      /**
       * Create the org
       */
      const newOrg = await apiClient({
        method: 'post',
        url: '/core/tenants',
        data: {
          ownerUserName: me.userName,
          title: orgName,
          tenantName: orgName,
        },
      })

      /**
       * Save the org name you're navigating to to localStorage,
       * so if you land back on the '/' path in the future we can
       * try to put you back here
       */
      session.set('selectedOrgName', orgName)

      /**
       * Hard refresh to the root of the new org
       */
      if (redirect) {
        window.location.href = `/${orgName}`
      }
      return newOrg
    } catch (err) {
      setIsCreating(false)

      throw new Error(getErrorMessage(err))
    }
  }

  return {
    isCreating,
    isValidating,
    isValid,
    errorMsg,
    createOrg,
  }
}
